/**
 * OutputIntegrity — post-processing sanity check for audio-processing tools.
 *
 * Audio editors (enhance, denoise, compress, EQ, pitch-preserving ops, ...)
 * transform a signal but MUST preserve its DURATION. A decoded result whose
 * length drifts from the input means the encode is corrupt — truncated,
 * bloated, or unreadable. Duration is the one invariant that catches every
 * corruption variant regardless of which engine produced it:
 *   - FFmpeg WASM OOM mid-encode → truncated tail
 *   - streaming pipeline chunk mis-assembly → bloated / wrong length
 *   - codec edge case → result that decodeAudioData() rejects outright
 *
 * On top of duration we also flag SILENT output: a result whose peak is
 * essentially zero (<-60 dBFS) while the input was audible. Triggered by
 * bug #495 (Роза, 2026-05-22) — Mp3DecoderStream returned garbage Float32
 * samples that biquad filters propagated as NaN, and the LAME encoder wrote
 * NaN as silence. Duration matched perfectly; the duration-only guard let
 * the silent file through. Once we also check peak amplitude, the entire
 * "valid-container-but-no-audio" failure mode becomes a single early signal.
 *
 * Without this check the failure is SILENT: the tool shows a result panel,
 * the user downloads a broken file, and any loudness/spectrogram readout
 * happily reports on garbage. This module turns silent corruption into a
 * detectable, honest signal.
 *
 * Trigger: bug #432 (/audio-enhancer, 2026-05-16) — a 0:35 source produced a
 * 0:05 silent result while the LUFS readout still cheered "+13.3 LU louder".
 *
 * Public API
 * ──────────
 *   OutputIntegrity.check(beforeBuf, afterBuf, opts?) → {
 *       corrupt:  boolean,
 *       reason:   'ok' | 'decode_failed' | 'truncated' | 'bloated' | 'silent_output' | 'unknown',
 *       beforeDur: number,   // seconds (0 when unavailable)
 *       afterDur:  number,   // seconds (0 when unavailable / decode failed)
 *       toleranceSec: number, // the drift threshold actually applied
 *       beforePeak: number,  // input |sample| max, 0..~1 (NaN if unavailable)
 *       afterPeak:  number   // output |sample| max, 0..~1 (NaN if unavailable)
 *   }
 *     beforeBuf / afterBuf — decoded AudioBuffers, or any object exposing a
 *       numeric `.duration`. A null/undefined afterBuf → reason 'decode_failed'.
 *       A null/undefined beforeBuf → cannot compare, corrupt:false (no false
 *       alarm — the caller simply has no reference).
 *     opts.tolerancePct     (default 0.03) — fractional duration drift allowed
 *     opts.toleranceMinSec  (default 0.5)  — absolute floor; absorbs MP3
 *                                            encoder/decoder priming delay
 *     opts.expectDurationChange (default false) — trim / loop / time-stretch
 *       tools legitimately change duration; pass true to skip the length test
 *       (then only decode_failed is reported).
 *     opts.silentPeakThreshold (default 0.001 = -60 dBFS) — peaks below this
 *       on the result, paired with an input peak above audibleInputThreshold,
 *       flag as 'silent_output'. Skipped if beforeBuf doesn't expose
 *       getChannelData (e.g. the caller passed a synthetic {duration} stub).
 *     opts.audibleInputThreshold (default 0.01 = -40 dBFS) — guard against
 *       false positives on a source that's already near-silent (a quiet
 *       passage, the user's first 3 s of background hum). Without this floor
 *       a silence-in / silence-out pipeline run would be flagged corrupt.
 *
 *   OutputIntegrity.decodeForCheck(input, audioContext) → Promise<AudioBuffer|null>
 *     Decode a Blob / File / ArrayBuffer / AudioBuffer to an AudioBuffer.
 *     Returns null on failure instead of throwing — a null return fed to
 *     check() as afterBuf is itself the 'decode_failed' signal.
 *
 *   OutputIntegrity.formatDuration(sec) → 'M:SS'
 *
 * Memory: O(1) — operates on already-decoded buffers, decodes nothing itself
 * unless decodeForCheck() is called. Peak measurement scans samples in-place;
 * O(N) time but no allocation.
 */
(function () {
    'use strict';

    var DEFAULTS = {
        tolerancePct: 0.03,
        toleranceMinSec: 0.5,
        expectDurationChange: false,
        silentPeakThreshold: 0.001,    // -60 dBFS
        audibleInputThreshold: 0.01,   // -40 dBFS
    };

    function _num(v) { return (typeof v === 'number' && isFinite(v)) ? v : NaN; }

    /**
     * Maximum |sample| across all channels of an AudioBuffer-like object.
     * NaN return = couldn't measure (e.g. caller passed a {duration} stub).
     * Linear-scan, no allocation.
     */
    function peakOf(buf) {
        if (!buf || typeof buf.getChannelData !== 'function') return NaN;
        var nCh = buf.numberOfChannels || 0;
        if (!nCh) return NaN;
        var peak = 0;
        for (var c = 0; c < nCh; c++) {
            var d;
            try { d = buf.getChannelData(c); } catch (_) { continue; }
            if (!d || !d.length) continue;
            for (var i = 0; i < d.length; i++) {
                var v = d[i];
                if (v !== v) continue;     // skip NaN, treat as 0
                if (v < 0) v = -v;
                if (v > peak) peak = v;
            }
        }
        return peak;
    }

    /**
     * Compare a processed result against its source. Pure function — no I/O.
     */
    function check(beforeBuf, afterBuf, opts) {
        opts = opts || {};
        var tolPct = (typeof opts.tolerancePct === 'number') ? opts.tolerancePct : DEFAULTS.tolerancePct;
        var tolMin = (typeof opts.toleranceMinSec === 'number') ? opts.toleranceMinSec : DEFAULTS.toleranceMinSec;
        var expectChange = !!opts.expectDurationChange;
        var silentPeak = (typeof opts.silentPeakThreshold === 'number') ? opts.silentPeakThreshold : DEFAULTS.silentPeakThreshold;
        var audibleIn = (typeof opts.audibleInputThreshold === 'number') ? opts.audibleInputThreshold : DEFAULTS.audibleInputThreshold;

        var beforeDur = beforeBuf ? _num(beforeBuf.duration) : NaN;
        var afterDur = afterBuf ? _num(afterBuf.duration) : NaN;
        var beforePeak = peakOf(beforeBuf);
        var afterPeak = peakOf(afterBuf);

        // The result file could not be decoded at all → unreadable / corrupt.
        if (!afterBuf || !isFinite(afterDur)) {
            return {
                corrupt: true, reason: 'decode_failed',
                beforeDur: isFinite(beforeDur) ? beforeDur : 0,
                afterDur: 0, toleranceSec: 0,
                beforePeak: beforePeak, afterPeak: NaN,
            };
        }

        // No reference to compare against — don't raise a false alarm.
        if (!beforeBuf || !isFinite(beforeDur) || beforeDur <= 0) {
            return {
                corrupt: false, reason: 'ok',
                beforeDur: isFinite(beforeDur) ? beforeDur : 0,
                afterDur: afterDur, toleranceSec: 0,
                beforePeak: beforePeak, afterPeak: afterPeak,
            };
        }

        var toleranceSec = Math.max(tolMin, beforeDur * tolPct);

        // Trim / loop / stretch tools legitimately change duration. Still
        // worth detecting silent_output even in that mode — trim/loop should
        // not zero out signal energy.
        var drift = afterDur - beforeDur;
        if (!expectChange && Math.abs(drift) > toleranceSec) {
            return {
                corrupt: true,
                reason: drift < 0 ? 'truncated' : 'bloated',
                beforeDur: beforeDur, afterDur: afterDur, toleranceSec: toleranceSec,
                beforePeak: beforePeak, afterPeak: afterPeak,
            };
        }

        // Silent-output guard. Only meaningful when:
        //   (1) we could measure both peaks (peakOf returned a number, not NaN),
        //   (2) the input had audible content (peak > audibleInputThreshold),
        //   (3) the output peak collapsed below silentPeakThreshold.
        // A silence-in / silence-out run is left as 'ok' — no false alarm.
        // Bug #495 root cause sat exactly here: 3-min music input → 3-min
        // silent output. Duration matched ⇒ pre-silence-guard check passed,
        // banner never appeared, user only noticed when opening the file
        // in an external player.
        if (isFinite(beforePeak) && isFinite(afterPeak)
                && beforePeak > audibleIn && afterPeak < silentPeak) {
            return {
                corrupt: true, reason: 'silent_output',
                beforeDur: beforeDur, afterDur: afterDur, toleranceSec: toleranceSec,
                beforePeak: beforePeak, afterPeak: afterPeak,
            };
        }

        return {
            corrupt: false, reason: 'ok',
            beforeDur: beforeDur, afterDur: afterDur, toleranceSec: toleranceSec,
            beforePeak: beforePeak, afterPeak: afterPeak,
        };
    }

    /**
     * Decode any audio input to an AudioBuffer. Returns null (never throws)
     * when the input cannot be decoded — that null is the corruption signal.
     */
    async function decodeForCheck(input, audioContext) {
        try {
            if (input && typeof input === 'object' && typeof input.getChannelData === 'function') {
                return input; // already an AudioBuffer
            }
            if (!audioContext) return null;
            var ab;
            if (input && typeof input.arrayBuffer === 'function') {
                ab = await input.arrayBuffer();
            } else if (input instanceof ArrayBuffer) {
                ab = input;
            } else {
                return null;
            }
            return await audioContext.decodeAudioData(ab);
        } catch (e) {
            return null;
        }
    }

    function formatDuration(sec) {
        if (typeof sec !== 'number' || !isFinite(sec) || sec < 0) sec = 0;
        var m = Math.floor(sec / 60);
        var s = Math.floor(sec % 60);
        return m + ':' + (s < 10 ? '0' : '') + s;
    }

    window.OutputIntegrity = {
        check: check,
        decodeForCheck: decodeForCheck,
        formatDuration: formatDuration,
        peakOf: peakOf,
        __isStub: false,
    };
})();
